import { useState } from 'react';
import { format } from 'date-fns';
import {
  Calendar,
  Flag,
  Hash,
  Info,
  Mail,
  MapPin,
  Phone,
  User,
} from 'lucide-react';

import {
  parseCalendarDate,
  ProfileCard,
  useGeoPicker,
} from '@sandtech-hub-monorepo/ui-kit-site';
import {
  CopyToClipboard,
  Separator,
  useDateFormat,
} from '@sandtech-hub-monorepo/ui-kit';
import { Gender } from '@sandtech-hub-monorepo/ehub-client';

import { formatPhoneNumber } from '../../../utils/formatPhoneNumber';
import { PersonalInfoEditDialog } from './PersonalInfoEditDialog';
import { PersonalInfoRow } from './PersonalInfoRow';

const genderLabels: Record<Gender, string> = {
  [Gender.Female]: 'Female',
  [Gender.Male]: 'Male',
  [Gender.NonBinary]: 'Non-binary',
  [Gender.Other]: 'Others',
  [Gender.PreferNotToSay]: 'Prefer not to say',
};

/*
 * No Preferred Name row: there is no `preferredName` field on the viewer for
 * this to read, and nothing in the edit dialog can set one, so the row was
 * permanently "—" behind an edit pencil that could not change it. It comes
 * back with the backend field.
 */
interface Props {
  sandId?: string | null;
  email?: string | null;
  birthDate?: string | null;
  gender?: Gender | null;
  phoneNumber?: string | null;
  countryOfOrigin?: string | null;
  countryOfResidence?: string | null;
  countyOfResidence?: string | null;
}

export const PersonalInfoSidebar = ({
  sandId,
  email,
  birthDate,
  gender,
  phoneNumber,
  countryOfOrigin,
  countryOfResidence,
  countyOfResidence,
}: Props) => {
  const { getCountryByCode, getStateByCodeAndCountry } = useGeoPicker();
  const [editOpen, setEditOpen] = useState(false);

  // birthDate arrives as YYYY-MM-DD. `new Date()` would read it as UTC
  // midnight and render the day before anywhere west of Greenwich.
  const parsedBirthDate = parseCalendarDate(birthDate);
  const dateFormat = useDateFormat();

  const openEdit = () => setEditOpen(true);
  const iconClass = 'h-5 w-5';

  // Matches ProfileHeader's "State, Country" formatting for the same pair of
  // fields, so the two displays of a learner's residence never disagree.
  const residenceLabel = [
    countryOfResidence && countyOfResidence
      ? getStateByCodeAndCountry(countyOfResidence, countryOfResidence)?.label
      : undefined,
    countryOfResidence
      ? getCountryByCode(countryOfResidence)?.label
      : undefined,
  ]
    .filter(Boolean)
    .join(', ');

  return (
    <div className="flex flex-col gap-4 w-full">
      <ProfileCard title="Personal Information" editable action={openEdit}>
        <div className="flex flex-col gap-5">
          <PersonalInfoRow
            icon={<Calendar className={iconClass} />}
            label="Date of Birth"
            value={
              parsedBirthDate ? format(parsedBirthDate, dateFormat) : undefined
            }
          />
          <PersonalInfoRow
            icon={<User className={iconClass} />}
            label="Gender"
            value={gender ? genderLabels[gender] : undefined}
          />
          <PersonalInfoRow
            icon={<Phone className={iconClass} />}
            label="Phone"
            value={formatPhoneNumber(phoneNumber)}
          />
          <PersonalInfoRow
            icon={<Flag className={iconClass} />}
            label="Nationality"
            value={
              countryOfOrigin
                ? getCountryByCode(countryOfOrigin)?.label
                : undefined
            }
          />
          <PersonalInfoRow
            icon={<MapPin className={iconClass} />}
            label="Current Location"
            value={residenceLabel || undefined}
          />
          {/*
            Separator marks where the card stops being editable: everything
            above changes through the header's pencil; Email and User ID
            below are read-only and have no row here that opens the dialog.
          */}
          <Separator />

          <PersonalInfoRow
            icon={<Mail className={iconClass} />}
            label="Email"
            value={email}
            action={email ? <CopyToClipboard text={email} /> : undefined}
          />

          {/*
            Not editable, and deliberately shown in full rather than truncated:
            the note below tells the learner this is their permanent
            identifier, which is only true of the whole value. It is what
            support asks for.
          */}
          <PersonalInfoRow
            icon={<Hash className={iconClass} />}
            label="User ID"
            value={sandId}
            valueClassName="font-mono text-xs break-all"
            action={sandId ? <CopyToClipboard text={sandId} /> : undefined}
          />

          <div className="flex items-center gap-4 rounded-lg border border-primary px-5 py-4">
            <div className="relative flex items-center justify-center size-[22px] shrink-0">
              <span className="absolute -inset-1 rounded-full border-2 border-primary opacity-30" />
              <span className="absolute -inset-[7px] rounded-full border-2 border-primary opacity-10" />
              <Info className="h-[22px] w-[22px] text-primary" />
            </div>
            <p className="text-sm text-foreground">
              Your User ID is your permanent identifier across the FIT platform
            </p>
          </div>
        </div>
      </ProfileCard>

      <PersonalInfoEditDialog
        open={editOpen}
        handleOpenChange={setEditOpen}
        defaultValues={{
          birthDate: parsedBirthDate ?? undefined,
          gender: gender || undefined,
          phoneNumber: phoneNumber || undefined,
          countryOfOrigin: countryOfOrigin || undefined,
          countryOfResidence: countryOfResidence || undefined,
          countyOfResidence: countyOfResidence || undefined,
        }}
      />
    </div>
  );
};
